<!-- src/apps/secret/components/conceal/landing/CtaSection.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  // Honest spec sheet: storage/cipher/read semantics as implemented.
  const facts: Array<{ key: string; value?: string; valueKey?: string }> = [
    { key: 'cipher', value: 'AES-256' },
    { key: 'passphrase', value: 'bcrypt' },
    { key: 'storage', valueKey: 'landing.cta.fact_storage' },
    { key: 'read', valueKey: 'landing.cta.fact_read' },
  ];

  const scrollToForm = () => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  };
</script>

<template>
  <section class="grid grid-cols-1 border-b border-day-line lg:grid-cols-2 dark:border-white/[0.07]">
    <div
      class="flex flex-col justify-center border-b border-day-line p-6 py-14 sm:p-9 lg:border-r lg:border-b-0 lg:py-24 lg:pr-16 dark:border-white/[0.07]">
      <h2
        class="mb-5 font-mono text-[28px] leading-[1.05] font-bold tracking-[-0.03em] text-day-ink lg:text-[clamp(30px,3.2vw,46px)] dark:text-night-ink">
        {{ t('landing.cta.title1') }}<br />
        <span class="text-brand-500">{{ t('landing.cta.title2') }}</span>
      </h2>
      <p class="mb-9 flex items-start gap-2.5 text-[15px] leading-[1.65] text-day-ink-muted dark:text-night-ink-muted">
        <span
          class="mt-2.5 h-px w-6 shrink-0 bg-brand-500"
          aria-hidden="true"></span>
        <span>{{ t('landing.cta.subtitle') }}</span>
      </p>
      <div>
        <button
          type="button"
          class="inline-flex items-center gap-2 bg-brand-600 px-6 py-3 font-mono text-[11px] tracking-[0.08em] text-white uppercase transition-colors hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-night"
          @click="scrollToForm">
          {{ t('landing.cta.button') }}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true">
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>

    <div
      class="flex flex-col justify-center bg-day-100 p-6 py-10 sm:p-9 lg:py-24 lg:pl-16 dark:bg-night-100">
      <dl>
        <div
          v-for="fact in facts"
          :key="fact.key"
          class="flex flex-wrap items-center justify-between gap-3 border-b border-day-line py-4 dark:border-white/[0.07]">
          <dt class="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.08em] text-day-ink-muted uppercase dark:text-night-ink-muted">
            <span
              class="inline-block size-1 shrink-0 rounded-full bg-brand-500"
              aria-hidden="true"></span>
            {{ t(`landing.cta.fact_${fact.key}_label`) }}
          </dt>
          <dd class="font-mono text-[13px] font-semibold tracking-[0.02em] text-day-ink dark:text-night-ink">
            {{ fact.valueKey ? t(fact.valueKey) : fact.value }}
          </dd>
        </div>
      </dl>
    </div>
  </section>
</template>
